import { InstallationFormModel } from '../forms'
import { getTreatHtcAsNonSgReady } from './treat-htc-as-non-sg-ready'

/**
 * If nominal power is higher than this limit, use the high power calculation.
 */
export const HIGH_NOMINAL_POWER_CALCULATION_LIMIT = 11000

const gzfMap: Record<number, number> = {
  2: 0.8,
  3: 0.75,
  4: 0.7,
  5: 0.65,
  6: 0.6,
  7: 0.55,
  8: 0.5,
  9: 0.45,
}

/** Get the GFZ value based on the number of devices */
const getGzfFromConsumersCount = (count: number): number => {
  if (count > 9) return 0.45

  if (gzfMap[count] === undefined) return 0

  return gzfMap[count]
}

/**
 * The power consumption limit is calculated based on the following formula:
 * If nominal power of a controller's controlled device if equal or lower than 11000 W,
 *
 * Limit = 4200 + (numberOfConsumers - 1) * gzf * 4200
 *
 * else, if it is higher than 11000 W
 *
 * Limit = max(0.4 * biggestConsumerInstalled, 0.4 * sumOfAirConditioningPower) + (numberOfConsumers - 1) * gzf * 4200
 *
 * biggestConsumerInstalled = max(max(SGReadyOutputs), maxStoragePower, max(wallboxMaxPower))
 *
 * SGReadyOutputs - Nominal power of SGReady behind HTC or Storage
 * maxStoragePower - Maximum charging power
 * wallboxMaxPower - wallbox protection (in A) * 230V
 * numberOfConsumers - Number of SGReady + Number of Storages + Number of Wallboxes
 */
export const getPowerConsumptionLimit = (installation: InstallationFormModel, htc320hNominalPower: number): number => {
  const { generalSettings, heatpump, ioInterfaces } = installation
  const treatHtcAsNonSgReady = getTreatHtcAsNonSgReady(generalSettings, ioInterfaces.storage, heatpump)

  const correctedHtcNominalPower = treatHtcAsNonSgReady ? 0 : htc320hNominalPower

  /**
   * Exclude MyPV controller from the number of consumer devices.
   * Also, HTC320H I/O device must exist for this calculation,
   * that is why +1 is added.
   */
  const totalIoControllers = 1 + (ioInterfaces.storage ? 1 : 0)

  const totalWallboxes = (installation.chargingStations.modbusTcp?.length ?? 0) + (installation.chargingStations.eebus?.length ?? 0)

  const totalStorages = installation.storageDevices.length

  const numberOfConsumers = totalWallboxes + totalIoControllers + totalStorages

  const gzf = getGzfFromConsumersCount(numberOfConsumers)

  if (correctedHtcNominalPower <= HIGH_NOMINAL_POWER_CALCULATION_LIMIT) {
    return Math.round(4200 + (numberOfConsumers - 1) * gzf * 4200)
  } else {
    // TODO: use BE value
    const sumOfAirConditioningPower = 0

    return Math.round(Math.max(0.4 * correctedHtcNominalPower, 0.4 * sumOfAirConditioningPower) + (numberOfConsumers - 1) * gzf * 4200)
  }
}
